import { Icon, Text, Menu, Button } from "@ttc3k/trekker";
import { Flex, Grid, styled } from "styled-system/jsx";
import { LogOut, UserScan } from "iconoir-react";
import { useAuth0 } from "@auth0/auth0-react";
import { useLoggedInAs, useLoginRedirectPath } from "shared/hooks";
import { EnumOwnerType } from "gql/generated.ts";
import {
  NavigationBlocker,
  OperatorMenu,
  OrganizationMenu,
  WhereaboutsLogoMark,
} from "shared/components";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useCurrentAppRoute } from "shared/hooks";
import { useAppContext } from "AppContext";

export const RootHeader = () => {
  const { t } = useTranslation();
  const { user, logout } = useAuth0();
  const loggedInAs = useLoggedInAs();
  const appRoute = useCurrentAppRoute();
  const navigate = useNavigate();
  const { clearLoginRedirectPath } = useLoginRedirectPath();

  const { operators, organizations } = useAppContext();
  const canLogginAsOtherUserType =
    loggedInAs?.type && operators.length + organizations.length > 1;
  const switchOperatorOrOrganizationText =
    operators.length > 0 && organizations.length > 0
      ? t("core:DOORSTOP.SWITCH.BOTH")
      : operators.length > 0
        ? t("core:DOORSTOP.SWITCH.OPERATOR")
        : organizations.length > 0
          ? t("core:DOORSTOP.SWITCH.ORGANIZATION")
          : "";

  /**
   * We only go back to a "home" screen if we're logged in as a user
   * - ORG users go to /app/organization/:organizationId/home
   * - OP users go to /app/operator
   * - If a user has both ORGs and OPs, and they are in as an OP,
   *   it will return them to the ORG user home route
   * @returns
   */
  const handleWhereaboutsClick = () => {
    const getNavTo = () => {
      const hasMainDoorStop =
        organizations.length > 1 ||
        (organizations.length > 0 && operators.length > 0);

      switch (loggedInAs?.type) {
        case EnumOwnerType.Organization:
          return hasMainDoorStop && appRoute.appPath === "home"
            ? "/app"
            : appRoute.appPath !== "home"
              ? `${appRoute.root}/home`
              : null;
        case EnumOwnerType.Operator:
          return hasMainDoorStop && appRoute.appPath === "operator"
            ? "/app"
            : appRoute.appPath !== "operator"
              ? appRoute.root
              : null;
        default:
          return hasMainDoorStop ? "/app" : null;
      }
    };
    const navTo = getNavTo();
    if (navTo) navigate(navTo);
  };

  // const handleSettingsClick = () => navigate(`${appRoute.root}/settings`);

  const handleDoorstopClick = () => navigate("/app");

  const handleLogout = () => {
    clearLoginRedirectPath();
    logout({
      logoutParams: { returnTo: window.location.origin },
    });
  };

  return (
    <Grid
      gridTemplateColumns={"50px 1fr 50px"}
      justifyContent={"space-between"}
      alignItems={"center"}
      gap="50"
      backgroundColor={"white"}
      px={"400"}
      borderBottomWidth={1}
      borderBottomColor={"border.light"}
    >
      {/* Whereabouts button */}
      <NavigationBlocker>
        <Button
          visual="unstyled"
          onClick={handleWhereaboutsClick}
          borderRadius={"25"}
        >
          <WhereaboutsLogoMark width={22} height="auto" />
        </Button>
      </NavigationBlocker>

      {/* Organization and Operator Menu */}
      <Flex
        flexDir={"row"}
        justifyContent={"center"}
        alignItems={"center"}
        gap={200}
        flex="1"
      >
        {loggedInAs?.type === EnumOwnerType.Operator && (
          <OperatorMenu operatorEntityId={loggedInAs.entityId} />
        )}
        {loggedInAs?.type === EnumOwnerType.Organization && (
          <OrganizationMenu organizationId={loggedInAs.entityId} />
        )}
      </Flex>

      {/* Settings and User Menu */}
      <Flex
        flexDir={"row"}
        justifyContent={"flex-end"}
        alignItems={"center"}
        gap={200}
      >
        {/*
         * Settings icon is currently removed, will keep this commented out
         * until a settings space is needed
         */}
        {/* {loggedInAs && loggedInAs.type === EnumOwnerType.Organization && (
          <>
            <NavigationBlocker>
              <Button visual="unstyled" onClick={handleSettingsClick} borderRadius={"full"}>
                <Icon Element={Settings} size={"lg"} />
              </Button>
            </NavigationBlocker>
            <Divider
              orientation={"vertical"}
              thickness={"2px"}
              height={"16px"}
              color={"border.light"}
            />
          </>
        )} */}

        {/* User Menu */}
        <Menu.Root
          trigger={
            <Button
              visual="unstyled"
              borderRadius={"full"}
              width={8}
              height={8}
            >
              {user?.picture ? (
                <styled.img
                  src={user?.picture}
                  borderRadius={"full"}
                  alt={user?.name}
                />
              ) : (
                <Text color={"ink.50"}>
                  {user?.name?.substring(0, 1).toUpperCase()}
                </Text>
              )}
            </Button>
          }
          positioning={{
            placement: "bottom-end",
            offset: {
              crossAxis: -64,
            },
          }}
        >
          <NavigationBlocker>
            {canLogginAsOtherUserType && (
              <Menu.Item value="doorstop" onClick={handleDoorstopClick}>
                <Text visual="smallRegular">
                  {switchOperatorOrOrganizationText}
                </Text>
                <Icon Element={UserScan} size="lg" />
              </Menu.Item>
            )}
            <Menu.Item value={"logout"} onClick={handleLogout}>
              <Text visual={"smallRegular"}>{t("core:LOGOUT")}</Text>
              <Icon Element={LogOut} size={"lg"} />
            </Menu.Item>
          </NavigationBlocker>
        </Menu.Root>
      </Flex>
    </Grid>
  );
};
